import { useCallback, useMemo, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { toasterMsg } from '../../../../../Components/Toaster/Toaster';
import { currencySymbols } from '../../../../../Components/helper';
import { useComponent } from '../../../../UserSetting/UsersLibrary/UserRights';
import * as actions from '../store/productPurchaseOrderTodo.action';
import { calculateTotalPrice } from './useProductPurchaseOrderTodo';

export const useProductPurchaseOrderLogic = ({
  state,
  setState,
  searchState,
  setSearchState,
  filterState,
  fetchPurchaseOrderTodos,
  debouncedSave,
  pendingUpdates,
  setPendingUpdates,
  debounceTimeoutRef,
  currentlyProcessingRef,
  pendingUpdatesRef,
  isSupplierLoggedIn,
  userInfo,
  logInRights,
  lengthVisible,
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation(['purchase-order', 'common']);
  const { CheckActionPermission } = useComponent();

  // Access permissions
  const PODeleteAccess = CheckActionPermission(logInRights, 'products', 'purchaseOrder', 'delete');
  const POAddEditAccess = CheckActionPermission(logInRights, 'products', 'purchaseOrder', 'addEdit');

  // Get dynamic currency symbol from userInfo
  const currencySymbol = useMemo(() => {
    const currency = userInfo?.companyCurrency;
    return currencySymbols[currency]?.symbol || '$';
  }, [userInfo?.companyCurrency]);

  // Column definitions
  const singleColumnKeysOrdered = useMemo(
    () => [
      'supplier',
      'factory',
      'templateName',
      'packType',
      'productNumber',
      'brand',
      'season',
      'month',
      'year',
      'category1',
      'category2',
      'category3',
      'category4',
      'colourway',
      'size',
      'length',
      'packRatio',
      'totalPacks',
      'location',
      'unitPrice',
      'discountPct',
      'priceAfterDiscount',
      'deliveryDate',
      'status',
    ],
    [],
  );

  const groupColumnDefsOrdered = useMemo(
    () => [
      { key: 'totalUnits', hasActual: true },
      { key: 'totalPrice', hasActual: true },
      { key: 'approvedByDate', hasActual: true },
      { key: 'exFactoryDate', hasActual: true },
      { key: 'shipmentDate', hasActual: true },
    ],
    [],
  );

  const allColumnKeys = useMemo(
    () => [...singleColumnKeysOrdered, ...groupColumnDefsOrdered.map((g) => g.key)],
    [singleColumnKeysOrdered, groupColumnDefsOrdered],
  );

  // Column dropdown options
  const columnDropdownOptions = useMemo(
    () => [
      { label: t('PURCHASE_ORDER.PRODUCT_PURCHASE_ORDER_TODO.COLUMNS.SUPPLIER'), value: 'supplier' },
      { label: t('PURCHASE_ORDER.PRODUCT_PURCHASE_ORDER_TODO.COLUMNS.FACTORY'), value: 'factory' },
      { label: t('PURCHASE_ORDER.PRODUCT_PURCHASE_ORDER_TODO.COLUMNS.TEMPLATE_NAME'), value: 'templateName' },
      { label: t('PURCHASE_ORDER.PRODUCT_PURCHASE_ORDER_TODO.COLUMNS.PACK_TYPE'), value: 'packType' },
      { label: t('PURCHASE_ORDER.PRODUCT_PURCHASE_ORDER_TODO.COLUMNS.PRODUCT_NUMBER'), value: 'productNumber' },
      { label: t('COMMON_LABEL.BRAND'), value: 'brand' },
      { label: t('COMMON_LABEL.SEASON'), value: 'season' },
      { label: t('COMMON_LABEL.MONTH'), value: 'month' },
      { label: t('COMMON_LABEL.YEAR'), value: 'year' },
      { label: t('COMMON_LABEL.CATEGORY_1'), value: 'category1' },
      { label: t('COMMON_LABEL.CATEGORY_2'), value: 'category2' },
      { label: t('COMMON_LABEL.CATEGORY_3'), value: 'category3' },
      { label: t('COMMON_LABEL.CATEGORY_4'), value: 'category4' },
      { label: t('PURCHASE_ORDER.PRODUCT_PURCHASE_ORDER_TODO.COLUMNS.COLOURWAY'), value: 'colourway' },
      { label: t('PURCHASE_ORDER.PRODUCT_PURCHASE_ORDER_TODO.COLUMNS.SIZE'), value: 'size' },
      { label: t('PURCHASE_ORDER.PRODUCT_PURCHASE_ORDER_TODO.COLUMNS.LENGTH'), value: 'length' },
      { label: t('PURCHASE_ORDER.PRODUCT_PURCHASE_ORDER_TODO.COLUMNS.SKU_NUMBER'), value: 'skuNumber' },
      { label: t('PURCHASE_ORDER.PRODUCT_PURCHASE_ORDER_TODO.COLUMNS.PACK_RATIO'), value: 'packRatio' },
      { label: t('PURCHASE_ORDER.PRODUCT_PURCHASE_ORDER_TODO.COLUMNS.TOTAL_PACKS'), value: 'totalPacks' },
      { label: t('PURCHASE_ORDER.PRODUCT_PURCHASE_ORDER_TODO.COLUMNS.TOTAL_UNITS'), value: 'totalUnits' },
      { label: t('PURCHASE_ORDER.PRODUCT_PURCHASE_ORDER_TODO.COLUMNS.LOCATION'), value: 'location' },
      { label: `${t('PURCHASE_ORDER.PRODUCT_PURCHASE_ORDER_TODO.COLUMNS.UNIT_PRICE')} (${currencySymbol})`, value: 'unitPrice' },
      { label: t('PURCHASE_ORDER.PRODUCT_PURCHASE_ORDER_TODO.COLUMNS.DISCOUNT_PCT'), value: 'discountPct' },
      { label: `${t('PURCHASE_ORDER.PRODUCT_PURCHASE_ORDER_TODO.COLUMNS.PRICE_AFTER_DISCOUNT')} (${currencySymbol})`, value: 'priceAfterDiscount' },
      { label: `${t('PURCHASE_ORDER.PRODUCT_PURCHASE_ORDER_TODO.COLUMNS.TOTAL_PRICE')} (${currencySymbol})`, value: 'totalPrice' },
      { label: t('PURCHASE_ORDER.PRODUCT_PURCHASE_ORDER_TODO.COLUMNS.APPROVE_BY_DATE'), value: 'approvedByDate' },
      { label: t('PURCHASE_ORDER.PRODUCT_PURCHASE_ORDER_TODO.COLUMNS.EX_FACTORY_DATE'), value: 'exFactoryDate' },
      { label: t('PURCHASE_ORDER.PRODUCT_PURCHASE_ORDER_TODO.COLUMNS.SHIPMENT_DATE'), value: 'shipmentDate' },
      { label: t('PURCHASE_ORDER.PRODUCT_PURCHASE_ORDER_TODO.COLUMNS.DELIVERY_DATE'), value: 'deliveryDate' },
    ],
    [t, currencySymbol],
  );

  // Column visibility helpers
  const isColVisible = useCallback((key) => state.visibleColumns?.[key] !== false, [state.visibleColumns]);
  const selectedColumns = useMemo(() => columnDropdownOptions.filter((opt) => isColVisible(opt.value)), [columnDropdownOptions, isColVisible]);

  // Build default visibility map or hydrate from localStorage
  useEffect(() => {
    const stored = localStorage.getItem('poTodoColumnVisibility');
    const defaults = allColumnKeys.reduce((acc, k) => ({ ...acc, [k]: true }), {});

    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        const mergedVisibility = { ...defaults, ...parsed };
        mergedVisibility.templateName = true;
        setState((prev) => ({ ...prev, visibleColumns: mergedVisibility }));
      } catch (_) {
        setState((prev) => ({ ...prev, visibleColumns: defaults }));
      }
    } else {
      setState((prev) => ({ ...prev, visibleColumns: defaults }));
    }
  }, [allColumnKeys, setState]);

  // Compute total visible columns
  const computeTotalVisibleColumns = useCallback(() => {
    let count = 5; // Fixed leading columns: select, expand, image, poRef, expandData

    // Single columns
    singleColumnKeysOrdered.forEach((key) => {
      if (state.visibleColumns?.[key] !== false) {
        if (key === 'length' && !lengthVisible) {
          return;
        }
        count += 1;
      }
    });

    // Group columns
    groupColumnDefsOrdered.forEach((g) => {
      if (state.visibleColumns?.[g.key] !== false) {
        count += 1; // original
        if (state.showRevisedColumns) count += 1;
        if (state.showActualColumns && g.hasActual) count += 1;
      }
    });

    count += 1; // action column
    return count;
  }, [state.visibleColumns, state.showRevisedColumns, state.showActualColumns, lengthVisible, singleColumnKeysOrdered, groupColumnDefsOrdered]);

  const totalVisibleColumns = useMemo(() => computeTotalVisibleColumns(), [computeTotalVisibleColumns]);

  // Event handlers
  const handleSelectedColumns = useCallback(
    (selected) => {
      const visibility = allColumnKeys.reduce((acc, k) => ({ ...acc, [k]: false }), {});
      selected.forEach((s) => {
        visibility[s.value] = true;
      });
      setState((prev) => ({ ...prev, visibleColumns: visibility }));
      localStorage.setItem('poTodoColumnVisibility', JSON.stringify(visibility));
    },
    [allColumnKeys, setState],
  );

  const handleSearch = useCallback(
    (e) => {
      const targetValue = e.target.value;
      setSearchState((prev) => ({
        ...prev,
        searchText: targetValue,
        searchLoaded: true,
      }));
    },
    [setSearchState],
  );

  const handleClearSearch = useCallback(() => {
    setSearchState((prev) => ({
      ...prev,
      searchText: '',
      searchLoaded: true,
    }));
  }, [setSearchState]);

  const handleBulkDelete = useCallback(() => {
    if (state.selectedIds.length === 0) {
      toasterMsg(t('PURCHASE_ORDER.PRODUCT_PURCHASE_ORDER_TODO.SELECT_ORDERS_TO_DELETE'), 'danger');
      return;
    }
    setState((prev) => ({
      ...prev,
      deleteMultipleModal: true,
      deletedId: [],
    }));
  }, [state.selectedIds, t, setState]);

  const handleSelectAllPO = useCallback(
    (e) => {
      if (e.target.checked) {
        setState((prev) => ({
          ...prev,
          selectedIds: prev.purchaseOrderTodos.map((order) => order._id),
          isSelectAll: true,
        }));
      } else {
        setState((prev) => ({
          ...prev,
          selectedIds: [],
          isSelectAll: false,
        }));
      }
    },
    [setState],
  );

  const handleExpandAllFalsePO = useCallback(() => {
    const updatedOrders = state.purchaseOrderTodos.map((order) => ({
      ...order,
      isExpanded: Array.isArray(order?.expandData) && order.expandData.length > 0 ? false : order.isExpanded,
    }));
    setState((prev) => ({
      ...prev,
      expandAll: false,
      purchaseOrderTodos: updatedOrders,
    }));
  }, [state.purchaseOrderTodos, setState]);

  const handleExpandAllTruePO = useCallback(() => {
    const updatedOrders = state.purchaseOrderTodos.map((order) => ({
      ...order,
      isExpanded: Array.isArray(order?.expandData) && order.expandData.length > 0 ? true : order.isExpanded,
    }));
    setState((prev) => ({
      ...prev,
      expandAll: true,
      purchaseOrderTodos: updatedOrders,
    }));
  }, [state.purchaseOrderTodos, setState]);

  const handleSelectPO = useCallback(
    (e) => {
      if (e.target.checked) {
        setState((prev) => ({
          ...prev,
          selectedIds: [...(prev?.selectedIds || []), e.target.value],
          isSelectAll: [...(prev?.selectedIds || []), e.target.value]?.length === prev?.purchaseOrderTodos?.length,
        }));
      } else {
        setState((prev) => ({
          ...prev,
          selectedIds: prev?.selectedIds.filter((id) => id !== e.target.value),
          isSelectAll: false,
        }));
      }
    },
    [setState],
  );

  const expandPO = useCallback(
    (id) => {
      setState((prev) => ({
        ...prev,
        purchaseOrderTodos: prev?.purchaseOrderTodos.map((order) => (order._id === id ? { ...order, isExpanded: !order.isExpanded } : order)),
      }));
    },
    [setState],
  );

  const handleRevisedColumns = useCallback(
    (e) => {
      setState((prev) => ({ ...prev, showRevisedColumns: e.target.checked }));
    },
    [setState],
  );

  const handleActualColumns = useCallback(
    (e) => {
      setState((prev) => ({ ...prev, showActualColumns: e.target.checked }));
    },
    [setState],
  );

  // Row and column editability helpers
  const isRowEditable = useCallback(
    (order) => {
      if (isSupplierLoggedIn) return false;
      const nonEditableStatuses = ['delivered', 'cancelled', 'rejected'];
      return !nonEditableStatuses.includes(order?.status?.toLowerCase());
    },
    [isSupplierLoggedIn],
  );

  const isColumnEditable = useCallback((columnKey) => {
    const editableColumns = ['actualTotalUnits', 'actualApprovedByDate', 'actualExFactoryDate', 'actualShipmentDate'];
    return editableColumns.includes(columnKey);
  }, []);

  // Auto-save functionality with debouncing
  const handleAutoSave = useCallback(
    (order, columnKey, value) => {
      if (!isRowEditable(order) || !isColumnEditable(columnKey)) {
        return;
      }

      const parentOrderId = order.purchaseOrderId || order._id;
      const detailIdFromOrder = order.orderDetailsId || order.orderDetails?.[0]?._id || order.originalOrderDetails?.[0]?._id;
      const updateKey = `${parentOrderId}-${columnKey}`;

      const updatedValues = { [columnKey]: value };
      const orderDetailsId = detailIdFromOrder;
      const isChildRow = !!orderDetailsId && order.purchaseOrderId;

      if (orderDetailsId) {
        updatedValues.orderDetailsId = orderDetailsId;
      }

      if (columnKey === 'actualTotalUnits') {
        const actualUnits = parseFloat(value || 0);
        const priceAfterDiscount = parseFloat(order.priceAfterDiscount || 0);
        const shippingCost = parseFloat(order.shippingHandlingCost || 0);
        const packType = order.packType || '';
        const ratio = parseFloat(order.packRatio || 0);
        const packs = parseFloat(order.totalPacks || 0);
        const calculatedPrice = calculateTotalPrice(packType, priceAfterDiscount, actualUnits, ratio, packs);
        updatedValues.actualTotalPrice = calculatedPrice + shippingCost;
      }

      if (isChildRow) {
        setState((prev) => ({
          ...prev,
          purchaseOrderTodos: prev.purchaseOrderTodos.map((o) => {
            if (o._id === parentOrderId && Array.isArray(o.expandData)) {
              return {
                ...o,
                isExpanded: o.isExpanded,
                expandData: o.expandData.map((expandRow) => {
                  const detail = expandRow?.orderDetails?.[0] || {};
                  const expandDetailId = detail?._id || expandRow?.orderDetailsId;
                  if (expandDetailId === orderDetailsId) {
                    return {
                      ...expandRow,
                      ...updatedValues,
                      orderDetails:
                        expandRow.orderDetails?.map((od) => {
                          if (od._id === orderDetailsId) {
                            return { ...od, ...updatedValues };
                          }
                          return od;
                        }) || expandRow.orderDetails,
                    };
                  }
                  return expandRow;
                }),
              };
            }
            return o;
          }),
        }));
      } else {
        setState((prev) => ({
          ...prev,
          purchaseOrderTodos: prev.purchaseOrderTodos.map((o) => (o._id === parentOrderId ? { ...o, isExpanded: o.isExpanded, ...updatedValues } : o)),
        }));
      }

      currentlyProcessingRef.current.add(updateKey);

      // For date fields (shared across group), immediately propagate to all detail rows of this PO
      if (columnKey === 'actualApprovedByDate' || columnKey === 'actualExFactoryDate' || columnKey === 'actualShipmentDate') {
        const parent = state.purchaseOrderTodos.find((o) => o._id === parentOrderId) || order;
        let allDetailIds = [];

        if (isChildRow && orderDetailsId) {
          allDetailIds = [orderDetailsId];
        } else if (Array.isArray(parent?.originalOrderDetails)) {
          allDetailIds = parent.originalOrderDetails.map((d) => d?._id).filter(Boolean);
        }

        if (allDetailIds.length === 0) {
          return;
        }

        const dateFieldUpdates = {};
        allDetailIds.forEach((detail) => {
          if (!dateFieldUpdates[parentOrderId]) {
            dateFieldUpdates[parentOrderId] = { childUpdates: {} };
          }
          if (!dateFieldUpdates[parentOrderId].childUpdates[detail]) {
            dateFieldUpdates[parentOrderId].childUpdates[detail] = {};
          }
          dateFieldUpdates[parentOrderId].childUpdates[detail][columnKey] = value;
        });

        dispatch(
          actions.batchUpdateProductPurchaseOrderTodoActualFields({
            updates: dateFieldUpdates,
            t,
          }),
        ).catch((error) => {
          console.error('Error updating date fields:', error);
        });

        return;
      }

      // Non-date fields (per-line) → queue debounced save
      setPendingUpdates((prev) => {
        const newPendingUpdates = {
          ...prev,
          [parentOrderId]: {
            ...prev[parentOrderId],
            ...(isChildRow && orderDetailsId
              ? {
                  childUpdates: {
                    ...prev[parentOrderId]?.childUpdates,
                    [orderDetailsId]: {
                      ...prev[parentOrderId]?.childUpdates?.[orderDetailsId],
                      ...updatedValues,
                    },
                  },
                }
              : updatedValues),
          },
        };
        pendingUpdatesRef.current = newPendingUpdates;
        return newPendingUpdates;
      });

      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }

      debounceTimeoutRef.current = setTimeout(() => {
        debouncedSave();
      }, 1000);
    },
    [
      isRowEditable,
      isColumnEditable,
      debouncedSave,
      setState,
      state.purchaseOrderTodos,
      currentlyProcessingRef,
      setPendingUpdates,
      pendingUpdatesRef,
      debounceTimeoutRef,
      dispatch,
      t,
    ],
  );

  const handlePageNextInScroll = async () => {
    if ((state?.currentPage || 1) * (state?.itemsPerPage || 20) < state?.totalItems && !state.isTableLoading) {
      try {
        const nextPage = state.currentPage + 1;
        setState((prev) => ({ ...prev, isTableLoading: true }));

        await dispatch(
          actions.fetchPurchaseOrderTodosList(
            {
              pageIndex: nextPage,
              pageSize: state.itemsPerPage,
              searchText: searchState?.searchText?.trim(),
              filters: {
                supplierIds: filterState?.activeSupplier || [],
                colourwayIds: filterState?.activeColourways || [],
                statuses: filterState?.activeStatus || [],
              },
              append: true,
            },
            t,
          ),
        );

        setState((prev) => ({
          ...prev,
          currentPage: nextPage,
        }));
      } catch (fetchError) {
        toasterMsg(t('PURCHASE_ORDER.PRODUCT_PURCHASE_ORDER_TODO.LOAD_MORE_ERROR'), 'danger');
      } finally {
        setState((prev) => ({ ...prev, isTableLoading: false }));
      }
    }
  };

  const handleDeleteMultiple = useCallback(
    (order) => {
      const arr = [];
      if (order) arr.push(order._id);
      setState((prevState) => ({
        ...prevState,
        deleteModal: !!order,
        deleteMultipleModal: !order,
        deletedId: arr,
      }));
    },
    [setState],
  );

  const handleApplyFilter = useCallback(
    (filterObj) => {
      const params = {
        filters: {
          supplierIds: filterObj.supplierId || [],
          colourwayIds: filterObj.colourwayId || [],
          statuses: filterObj.status || [],
        },
        pageIndex: 1,
        pageSize: state.itemsPerPage,
        searchText: searchState?.searchText?.trim(),
      };
      setState((prev) => ({
        ...prev,
        currentPage: 1,
        selectedIds: [],
        isSelectAll: false,
      }));

      fetchPurchaseOrderTodos(params);
    },
    [searchState?.searchText, state.itemsPerPage, setState, fetchPurchaseOrderTodos],
  );

  const handleCloseDeleteModal = useCallback(() => {
    setState((prevState) => ({
      ...prevState,
      deletedId: [],
      deleteModal: false,
      deleteMultipleModal: false,
    }));
  }, [setState]);

  const confirmDelete = useCallback(async () => {
    const { deletedId } = state;
    const idsToDelete = deletedId.length > 0 ? deletedId : state.selectedIds;
    if (idsToDelete.length === 0) return;

    try {
      setState((prev) => ({ ...prev, loading: true }));

      let response;
      if (idsToDelete.length === 1 && deletedId.length > 0) {
        response = await dispatch(actions.deleteProductPurchaseOrderTodo(idsToDelete[0], t));
      } else {
        response = await dispatch(actions.bulkDeleteProductPurchaseOrderTodos(idsToDelete, t));
      }

      if (response?.code === 200) {
        await fetchPurchaseOrderTodos();
        setState((prev) => ({
          ...prev,
          selectedIds: [],
          isSelectAll: false,
        }));
        handleCloseDeleteModal();
      }
    } catch (deleteError) {
      // Error message is already handled in the action
    } finally {
      setState((prev) => ({ ...prev, loading: false }));
    }
  }, [state, setState, dispatch, t, fetchPurchaseOrderTodos, handleCloseDeleteModal]);

  return {
    // Computed values
    currencySymbol,
    columnDropdownOptions,
    selectedColumns,
    totalVisibleColumns,
    PODeleteAccess,
    POAddEditAccess,

    // Helper functions
    isColVisible,
    isRowEditable,
    isColumnEditable,

    // Event handlers
    handleSelectedColumns,
    handleSearch,
    handleClearSearch,
    handleBulkDelete,
    handleSelectAllPO,
    handleExpandAllFalsePO,
    handleExpandAllTruePO,
    handleSelectPO,
    expandPO,
    handleRevisedColumns,
    handleActualColumns,
    handleAutoSave,
    handlePageNextInScroll,
    handleDeleteMultiple,
    handleApplyFilter,
    handleCloseDeleteModal,
    confirmDelete,
  };
};

export default useProductPurchaseOrderLogic;
